import { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import {
    LayoutDashboard,
    FolderKanban,
    Users,
    UserCog,
    BarChart3,
    Settings,
    Menu,
    X,
    LogOut,
    Globe,
    KeyRound,
    Building2,
    Stethoscope,
    MapPin,
} from "lucide-react";
import { LeafMark } from "@/components/LeafMark";
import { useAdminAuth } from "./AdminAuthContext";
import { ChangePasswordDialog } from "./ChangePasswordDialog";

const NAV = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-link-dashboard" },
    { to: "/admin/cases", label: "Cases", icon: FolderKanban, testid: "nav-link-cases" },
    { to: "/admin/patients", label: "Patients", icon: Users, testid: "nav-link-patients" },
    { to: "/admin/hospitals", label: "Hospitals", icon: Building2, testid: "nav-link-hospitals" },
    { to: "/admin/specialties", label: "Specialties", icon: Stethoscope, testid: "nav-link-specialties" },
    { to: "/admin/destinations", label: "Destinations", icon: MapPin, testid: "nav-link-destinations" },
    { to: "/admin/coordinators", label: "Coordinators", icon: UserCog, adminOnly: true, testid: "nav-link-coordinators" },
    { to: "/admin/analytics", label: "Analytics", icon: BarChart3, testid: "nav-link-analytics" },
    { to: "/admin/settings", label: "Settings", icon: Settings, adminOnly: true, testid: "nav-link-settings" },
];

const SidebarContent = ({ onNavigate }) => {
    const { user, logout } = useAdminAuth();
    const [pwOpen, setPwOpen] = useState(false);
    const items = NAV.filter((n) => !n.adminOnly || user?.role === "ADMIN");
    return (
        <div className="flex h-full flex-col justify-between">
            <div>
                <div className="px-6 pt-7 pb-6 border-b border-[#0B5148]/50">
                    <Link to="/" className="flex items-center gap-3" aria-label="UniversHeal — website">
                        <LeafMark className="w-8 h-8 text-[#F7F5EF]" />
                        <span>
                            <span className="block text-[#F7F5EF] font-bold tracking-[0.18em] text-sm">
                                UNIVERSHEAL
                            </span>
                            <span className="block text-[10px] uppercase tracking-[0.22em] text-[#A3B8B0] mt-0.5">
                                Operations Console
                            </span>
                        </span>
                    </Link>
                </div>
                <nav className="mt-6 px-3 space-y-1" data-testid="admin-sidebar-nav">
                    {items.map(({ to, label, icon: Icon, end, testid }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={end}
                            data-testid={testid}
                            onClick={onNavigate}
                            className={({ isActive }) =>
                                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors duration-200 ${
                                    isActive
                                        ? "bg-[#0B5148] text-[#F7F5EF] font-semibold shadow-inner"
                                        : "text-[#A3B8B0] hover:bg-[#0B5148]/50 hover:text-[#F7F5EF]"
                                }`
                            }
                        >
                            <Icon className="w-4 h-4 shrink-0" />
                            {label}
                        </NavLink>
                    ))}
                </nav>
            </div>
            <div className="px-3 pb-6 space-y-2">
                <Link
                    to="/"
                    data-testid="admin-view-website-link"
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-[#A3B8B0] hover:bg-[#0B5148]/50 hover:text-[#F7F5EF] transition-colors duration-200"
                >
                    <Globe className="w-4 h-4 shrink-0" />
                    View website
                </Link>
                <div className="rounded-lg bg-[#0B5148]/40 border border-[#0B5148]/50 px-3 py-3">
                    <p className="text-sm font-semibold text-[#F7F5EF] truncate" data-testid="user-name">
                        {user?.name}
                    </p>
                    <span
                        data-testid="user-role-badge"
                        className="mt-1 inline-block rounded border border-emerald-800/70 bg-emerald-950/60 px-1.5 py-0.5 text-[10px] font-bold tracking-wider text-emerald-300"
                    >
                        {user?.role}
                    </span>
                </div>
                <button
                    type="button"
                    data-testid="change-password-button"
                    onClick={() => setPwOpen(true)}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-[#A3B8B0] hover:bg-[#0B5148]/50 hover:text-[#F7F5EF] transition-colors duration-200"
                >
                    <KeyRound className="w-4 h-4 shrink-0" />
                    Change password
                </button>
                <button
                    type="button"
                    data-testid="admin-logout-button"
                    onClick={logout}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-[#A3B8B0] hover:bg-[#0B5148]/50 hover:text-[#F7F5EF] transition-colors duration-200"
                >
                    <LogOut className="w-4 h-4 shrink-0" />
                    Sign out
                </button>
            </div>
            <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
        </div>
    );
};

export function AdminShell() {
    const [mobileOpen, setMobileOpen] = useState(false);
    return (
        <div className="min-h-screen bg-[#0B1513]" data-testid="admin-shell">
            {/* Desktop sidebar */}
            <aside
                data-testid="admin-sidebar"
                className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-[#063C35] border-r border-[#0B5148]/50 shadow-2xl z-40"
            >
                <SidebarContent />
            </aside>

            {/* Mobile drawer */}
            {mobileOpen && (
                <div className="lg:hidden fixed inset-0 z-50">
                    <button
                        type="button"
                        data-testid="admin-sidebar-backdrop"
                        aria-label="Close menu"
                        onClick={() => setMobileOpen(false)}
                        className="absolute inset-0 bg-black/60"
                    />
                    <aside className="absolute inset-y-0 left-0 w-64 bg-[#063C35] border-r border-[#0B5148]/50 shadow-2xl">
                        <SidebarContent onNavigate={() => setMobileOpen(false)} />
                    </aside>
                </div>
            )}

            <div className="lg:pl-64">
                {/* Topbar */}
                <header
                    data-testid="admin-topbar"
                    className="sticky top-0 z-30 flex items-center gap-4 border-b border-[#1D322E] bg-[#0B1513]/90 backdrop-blur px-4 sm:px-6 py-3"
                >
                    <button
                        type="button"
                        data-testid="admin-mobile-menu-button"
                        aria-label="Open menu"
                        onClick={() => setMobileOpen(true)}
                        className="lg:hidden rounded-lg border border-[#1D322E] p-2 text-[#A3B8B0] hover:text-[#F7F5EF] hover:border-[#0B5148] transition-colors"
                    >
                        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                    <span className="lg:hidden flex items-center gap-2 text-[#F7F5EF] font-bold tracking-[0.18em] text-sm">
                        <LeafMark className="w-5 h-5" /> UNIVERSHEAL
                    </span>
                    <span className="hidden lg:block text-xs uppercase tracking-[0.22em] text-[#64716D]">
                        Staff portal — internal use only
                    </span>
                </header>

                <main className="px-4 sm:px-6 lg:px-8 py-8 max-w-7xl mx-auto" data-testid="admin-main">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
